import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  inject,
  Input,
  input,
  OnDestroy,
  OnInit,
  output,
} from '@angular/core';
import {
  AbstractControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { pairwise, takeUntil } from 'rxjs/operators';
import { Schema } from '@app/core/_models/schema';
import { SettingsService } from '@app/core/settings/settings.service';
import { BaseParamsService } from '@app/core/base-params.service';
import { IdentifierType } from '@app/core/_models/member-interface';
import { AbstractControlValueAccessor } from '@app/shared/abstract_control_value_accessor.abstract';
import { SchemaTranslations } from '@app/shared/models/schema-translations';
import { MsisdnInputComponent } from '@app/shared/components/msisdn-input/msisdn-input.component';
import { PropertyCompositeDateComponent } from '@app/+profile-form/components/properties-form/property-composite-date/property-composite-date.component';
import { PropertyDateComponent } from '@app/+profile-form/components/properties-form/property-date/property-date.component';
import { PropertySingleEnumComponent } from '@app/+profile-form/components/properties-form/property-single-enum/property-single-enum.component';
import { PropertyGroupedHashComponent } from '@app/+profile-form/components/properties-form/property-grouped-hash/property-grouped-hash.component';
import { PropertyArrayCustomComponent } from '@app/+profile-form/components/properties-form/property-array/property-array-custom/property-array-custom.component';
import { PropertyArrayFixedComponent } from '@app/+profile-form/components/properties-form/property-array/property-array-fixed/property-array-fixed.component';
import { PropertyArrayDynamicComponent } from '@app/+profile-form/components/properties-form/property-array/property-array-dynamic/property-array-dynamic.component';
import { PropertyNormalInputComponent } from '@app/+profile-form/components/properties-form/property-normal-input/property-normal-input.component';
import { PasswordInputConfirmationComponent } from '@app/+profile-form/components/properties-form/password-input-confirmation/password-input-confirmation.component';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { InputErrorsComponent } from '@app/shared/components/input-errors/input-errors.component';
import { AsyncPipe } from '@angular/common';
import { TranslationsService } from '@app/core/settings/translations.service';
import { ProfileErrors } from '@app/+profile-form/views/profile-errors-helpers';

enum IntlValidationError {
  IS_POSSIBLE = 0,
  INVALID_COUNTRY_CODE = 1,
  TOO_SHORT = 2,
  TOO_LONG = 3,
  IS_POSSIBLE_LOCAL_ONLY = 4,
  INVALID_LENGTH = 5,
}

type ControlError = { error: string; [key: string]: unknown };

@Component({
  selector: 'app-properties-form',
  templateUrl: './properties-form.component.html',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PropertiesFormComponent), multi: true },
  ],
  imports: [
    MsisdnInputComponent,
    PropertyCompositeDateComponent,
    ReactiveFormsModule,
    PropertyDateComponent,
    PropertySingleEnumComponent,
    PropertyGroupedHashComponent,
    PropertyArrayCustomComponent,
    PropertyArrayFixedComponent,
    PropertyArrayDynamicComponent,
    PropertyNormalInputComponent,
    PasswordInputConfirmationComponent,
    RouterLink,
    TranslatePipe,
    InputErrorsComponent,
    AsyncPipe,
  ],
})
export class PropertiesFormComponent extends AbstractControlValueAccessor implements OnInit, OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private settingsService = inject(SettingsService);
  private baseParamsService = inject(BaseParamsService);

  // TODO migrate manually to signals
  @Input() public propertyNames: string[];
  public readonly properties = input.required<Schema>();
  public readonly consents = input<Record<string, unknown>>(undefined!);
  public readonly identifiers = input<IdentifierType[]>();
  public readonly isNewProfile = input(false);
  public readonly isFormSubmitted = input(false);
  public readonly isForcedUpdate = input(false);

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input()
  public set formErrors(errors: ProfileErrors) {this._apiErrors = errors;}

  public get apiErrors(): ProfileErrors {return this._apiErrors;}

  public readonly save = output<any>();

  public form: UntypedFormGroup;

  public schemaTranslations$: Observable<SchemaTranslations> = inject(TranslationsService).getSchemaTranslations();

  public settings = this.settingsService.settings;

  public isUserPasswordAvailable =
    typeof this.settings.schema.identifiers !== 'undefined' &&
    this.settings.schema.identifiers.includes('email') &&
    !this.settings.schemaObject.hasMultipleIdentifiers(); // hybrid clubs do not use conventional passwords

  private readonly unsubscribe$ = new Subject();
  private _apiErrors: ProfileErrors = {};

  public ngOnInit(): void {
    this.propertyNames = this.propertyNames ?? this.getVisibleFieldsInOrder();
    this.createForm();
    this.cdr.detectChanges();

    this.form.valueChanges
      .pipe(
        pairwise(),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(([oldValue, newValue]) => {
        this.mapApiErrors(oldValue, newValue);
        this.onTouched();
        this.onChange(newValue);
      });
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  public writeValue(obj: any): void {
    this.form.patchValue(obj);
  }

  public getVisibleFieldsInOrder(): string[] {
    return this.getFieldsInOrder().filter((field) => this.properties().isVisible(field));
  }

  public isPropertyReadonly(propertyName: string): boolean {
    return !this.isNewProfile() && !this.isForcedUpdate() && this.isIdentifier(propertyName) && !this.properties().hasMultipleIdentifiers();
  }

  private getFieldsInOrder(): string[] {
    return this.properties().getAvailableFieldNames(this.baseParamsService.source);
  }

  private createForm(): void {
    this.form = new UntypedFormGroup({
        password: new UntypedFormControl(''),
      }, this.propertiesFormValidator.bind(this),
    );

    for (const fieldName of this.properties().getAvailableFieldNames(this.baseParamsService.source)) {
      this.form.addControl(fieldName, new UntypedFormControl(''));

      if (this.properties().isFieldRequired(fieldName)) {
        this.form.get(fieldName)?.setValidators(Validators.required);
      }
    }
  }

  private propertiesFormValidator(control: AbstractControl): ValidationErrors | null {
    const formGroup = control as UntypedFormGroup;
    const errors: Record<string, unknown> = {};

    Object.keys(formGroup.controls).forEach(formControlName => {
      const formControl: AbstractControl | null = formGroup.get(formControlName);

      if (formControl?.errors) {

        if (formControlName === 'password') {
          errors[formControlName] = formControl.errors;
        }

        if (formControl.errors.required) {
          errors[formControlName] = { error: 'not_contain_required_property', property: formControlName };
        }
      }
    });
    const consents = this.consents();
    if (consents && Object.keys(consents).length) {
      // eslint-disable-next-line no-prototype-builtins
      if (formGroup.value.hasOwnProperty('email')) {
        if (consents.email_marketing && !formGroup.value.email) {
          errors.email = { error: 'email_marketing_not_contain_required_property', property: 'email' };
        }
      }

      // eslint-disable-next-line no-prototype-builtins
      if (formGroup.value.hasOwnProperty('msisdn')) {
        if (consents.sms_marketing && !formGroup.value.msisdn) {
          errors.msisdn = { error: 'sms_marketing_not_contain_required_property', property: 'msisdn' };
        }
      }
    }
    this.cdr.markForCheck();
    return Object.keys(errors).length ? errors : null;
  }

  public isIdentifier(controlName: string): boolean {
    if (!this.identifiers()) {
      return controlName === IdentifierType.MSISDN;
    }

    return this.properties().isIdentifier(controlName);
  }


  public isMsisdnField(controlName: string): boolean {
    if (!this.identifiers()) {
      return controlName === IdentifierType.MSISDN;
    }

    return this.properties().isMsisdnField(controlName);
  }

  public isEmailField(controlName: string): boolean {
    if (!this.identifiers()) {
      return controlName === IdentifierType.EMAIL;
    }

    return this.properties().isEmailField(controlName);
  }

  protected mapControlError(
    controlName: string,
    errors: ValidationErrors | null,
  ): ControlError | null {
    if (!errors) {
      return null;
    }

    switch (controlName) {
      case IdentifierType.MSISDN: {
        return this.mapMsisdnControlErrors(errors);
      }
      default: {
        return this.mapDefaultControlErrors(errors);
      }
    }
  }

  private mapApiErrors(oldValue: any, newValue: any): void {
    Object.keys(this.apiErrors).forEach(key => {
      if (oldValue[key] !== newValue[key]) {
        this.apiErrors[key] = undefined;
      }
    });
  }

  private mapMsisdnControlErrors(errors: ValidationErrors): ControlError | null {
    const key = Object.keys(errors)[0];
    if (!key) {
      return null;
    }

    if (key === 'error') {
      switch (errors[key]) {
        case IntlValidationError.INVALID_COUNTRY_CODE:
          return { error: 'msisdn_invalid_country_code' };
        case IntlValidationError.TOO_SHORT:
          return { error: 'msisdn_too_short' };
        case IntlValidationError.TOO_LONG:
          return { error: 'msisdn_too_long' };
        case IntlValidationError.IS_POSSIBLE_LOCAL_ONLY:
          return { error: 'msisdn_is_possible_local_only' };
        case IntlValidationError.INVALID_LENGTH:
          return { error: 'msisdn_invalid_length' };
        default:
          return { error: 'msisdn_unknown' };
      }
    }

    return this.mapDefaultControlErrors(errors);
  }

  private mapDefaultControlErrors(errors: ValidationErrors): ControlError | null {
    const key = Object.keys(errors)[0];
    if (!key) {
      return null;
    }

    return {
      ...errors[key] !== null && typeof errors[key] === 'object'
        ? errors[key]
        : {},
      error: key,
    };
  }
}
